"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import type { BlogData } from "@/types/blog";
import renderOembed from "@/lib/iframeFunction";

interface BlogShowProps {
  blog: BlogData;
}

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
  number: string;
}

export default function BlogShow({ blog }: BlogShowProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [tableOfContents, setTableOfContents] = useState<TableOfContentsItem[]>([]);
  const headingElementsRef = useRef<Map<string, HTMLElement>>(new Map());

  // Generate slug from text
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  // Process HTML content to add IDs to headings and extract TOC
  useEffect(() => {
    if (!contentRef.current) return;

    let processed = false;

    // Use multiple attempts to ensure DOM is fully rendered
    const processHeadings = () => {
      const content = contentRef.current;
      if (!content) return;

      // Only get headings from within the content area (not the main title)
      const headings = content.querySelectorAll("h1, h2, h3, h4, h5, h6");
      if (headings.length === 0) {
        console.log("⏳ No headings found yet, waiting...");
        return; // No headings found yet
      }

      console.log(`📝 Processing ${headings.length} headings...`);

      const toc: TableOfContentsItem[] = [];
      const counters = [0, 0, 0, 0, 0, 0]; // Counters for each level (h1-h6)

      headings.forEach((heading, index) => {
        const text = heading.textContent || "";
        if (!text.trim()) return; // Skip empty headings

        const level = parseInt(heading.tagName.charAt(1));
        
        // Update counters based on level
        counters[level - 1]++;
        // Reset lower level counters when we encounter a higher level
        for (let i = level; i < 6; i++) {
          counters[i] = 0;
        }
        
        // Generate number string (e.g., "1", "1.1", "1.1.1")
        let numberStr = "";
        for (let i = 0; i < level; i++) {
          if (counters[i] > 0) {
            if (numberStr) numberStr += ".";
            numberStr += counters[i].toString();
          }
        }
        
        // Create a unique ID based on text and index
        const slug = generateSlug(text);
        const id = `toc-${slug}-${index}`;

        // Set ID on heading - THIS IS CRITICAL FOR LINKING
        const headingElement = heading as HTMLElement;
        
        // Only set if not already set to avoid duplicates
        if (!headingElement.id || headingElement.id !== id) {
          headingElement.id = id;
          headingElement.setAttribute('data-toc-id', id);
          headingElement.setAttribute('data-toc-number', numberStr);
          
          // Add scroll margin for better scroll positioning
          headingElement.style.scrollMarginTop = "100px";
          headingElement.style.scrollMarginBlockStart = "100px";
          
          // Add a class for styling
          headingElement.classList.add('toc-heading');
          
          console.log(`✅ Set ID "${id}" on heading: ${text.substring(0, 30)}...`);
        }
        
        // Store reference to the element
        headingElementsRef.current.set(id, headingElement);

        // Add to TOC
        toc.push({
          id,
          text: text.trim(),
          level,
          number: numberStr,
        });
      });

      if (toc.length > 0 && !processed) {
        console.log(`📋 Generated TOC with ${toc.length} items`);
        setTableOfContents(toc);
        processed = true;
      }
    };

    // Try immediately
    processHeadings();

    // Use MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      processHeadings();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
    }

    // Also try after delays in case content loads asynchronously
    const timer1 = setTimeout(processHeadings, 300);
    const timer2 = setTimeout(processHeadings, 1000);
    const timer3 = setTimeout(processHeadings, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [blog.description]);

  // Smooth scroll to heading
  const scrollToHeading = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    
    console.log("🔍 Looking for heading with ID:", id);
    
    // Try to find the element
    let element: HTMLElement | null = headingElementsRef.current.get(id) || null;
    
    if (!element) {
      element = document.getElementById(id);
    }
    
    if (!element && contentRef.current) {
      element = contentRef.current.querySelector(`#${id}`) as HTMLElement;
    }
    
    if (!element) {
      element = document.querySelector(`[data-toc-id="${id}"]`) as HTMLElement;
    }
    
    if (element) {
      console.log("✅ Element found:", element.textContent?.substring(0, 50));
      
      // Calculate position - use getBoundingClientRect for accuracy
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const offset = 100;
      const targetPosition = rect.top + scrollTop - offset;
      
      console.log("📍 Scroll details:", {
        rectTop: rect.top,
        scrollTop: scrollTop,
        offset: offset,
        targetPosition: targetPosition,
        elementOffsetTop: element.offsetTop
      });
      
      // Scroll to the element
      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: "smooth",
      });
    } else {
      console.error("❌ Element NOT FOUND with ID:", id);
      // Show all available IDs for debugging
      if (contentRef.current) {
        const allHeadings = contentRef.current.querySelectorAll("h1, h2, h3, h4, h5, h6");
        const availableIds = Array.from(allHeadings).map(h => (h as HTMLElement).id).filter(Boolean);
        console.log("📋 Available heading IDs:", availableIds);
      }
    }
  };

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-purple-800 mb-4 leading-tight">
        {blog.title}
      </h1>

      {blog.date && (
        <p className="text-gray-500 text-sm sm:text-base mb-6">
          {new Date(blog.date).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
          {blog.writer && (
            <span className="text-purple-700 pl-6">by {blog.writer}</span>
          )}
        </p>
      )}

      {blog.image && (
        <div className="w-full h-64 sm:h-80 md:h-96 mb-8 overflow-hidden rounded-2xl shadow-lg relative">
          <Image
            src={`https://www.itscoupons.com${blog.image}`}
            alt={blog.title}
            title={blog.title}
            width={1200}
            height={675}
            sizes="(max-width: 768px) 100vw, 1200px"
            className="object-cover object-center transition-transform duration-500 hover:scale-105"
            priority
          />
        </div>
      )}

      {/* Table of Contents */}
      {tableOfContents.length > 0 && (
        <div className="mb-10 p-5 bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl shadow-md">
          <h2 className="text-xl font-bold text-purple-800 mb-4">Table of Contents</h2>
          <nav className="space-y-1">
            {tableOfContents.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => scrollToHeading(item.id, e)}
                className={`block w-full text-left transition-all duration-150 rounded-md hover:bg-purple-100 hover:text-purple-900 cursor-pointer ${
                  item.level === 1
                    ? "font-bold text-sm text-purple-800 px-3 py-2.5"
                    : item.level === 2
                    ? "font-medium text-sm text-purple-700 pl-6 pr-3 py-2"
                    : item.level === 3
                    ? "font-normal text-xs text-purple-600 pl-9 pr-3 py-1.5"
                    : "font-normal text-xs text-purple-500 pl-12 pr-3 py-1"
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <span className="text-purple-500 font-medium text-xs flex-shrink-0 w-8">
                    {item.number}
                  </span>
                  <span className="flex-1 leading-snug">{item.text}</span>
                </span>
              </a>
            ))}
          </nav>
        </div>
      )}

      <div
        ref={contentRef}
        className="prose prose-purple max-w-full sm:prose-lg md:prose-xl mx-auto"
        style={{ scrollBehavior: 'smooth' }}
        dangerouslySetInnerHTML={{
          __html: renderOembed(blog.description) || "<p>No description available.</p>",
        }}
      />
    </section>
  );
}
